import dayjs from "dayjs";
import React from "react";
import {
  ChartComponentProps,
  ChartConfig,
  createChartConfig,
  TimeSeriesDataPoint,
} from "src/components/Referral-partner/Analytics/analyticsTypes";
import CustomerGrowth from "src/components/Referral-partner/Analytics/charts/CustomerGrowth";
import { WarningWrapper } from "src/components/Referral-partner/Analytics/styles/shared.styles";
import api from "src/store/features/api";
import { ReferralGrowthObj } from "src/store/features/api/generated";
import { useRPAnalyticsState } from "src/store/features/RPAnalytics.slice";

const useGetGrowthHistoryConfig = (): { growthHistory: ChartConfig<TimeSeriesDataPoint> } => {
  const { chartDateRange } = useRPAnalyticsState();

  // fetch data and states
  const {
    data: rawGrowthHistory,
    isLoading: growthIsLoading,
    isFetching: growthIsFetching,
    isError: growthIsError,
    error: growthError,
  } = api.useGetReferralGrowthHistoryQuery({
    timeFrame: chartDateRange.growthHistory,
    timezone: dayjs.tz.guess(),
  });

  // format data
  const growthHistoryData = React.useMemo((): TimeSeriesDataPoint[] => {
    if (!rawGrowthHistory) return [];

    return rawGrowthHistory.data
      .map((growth: ReferralGrowthObj): TimeSeriesDataPoint => {
        return {
          timestamp: growth.timestamp,
          customers: growth.count,
        };
      })
      .filter((growth) => growth.customers !== 0);
  }, [rawGrowthHistory]);

  // create chart config
  const growthHistoryConfig = React.useMemo(
    (): { growthHistory: ChartConfig<TimeSeriesDataPoint> } => ({
      growthHistory: createChartConfig<TimeSeriesDataPoint>({
        title: "Customer Growth Over Time",
        type: "line",
        menuMode: "compact",
        defaultDate: "all time",
        tooltip: "",
        isLoading: growthIsLoading || growthIsFetching,
        data: growthHistoryData,
        component: ({ data }: ChartComponentProps<TimeSeriesDataPoint>): JSX.Element => {
          if (growthIsError) {
            return <WarningWrapper>{growthError?.message}</WarningWrapper>;
          } else if (data.length <= 0) {
            return <WarningWrapper color="#002035">No Data</WarningWrapper>;
          } else {
            return <CustomerGrowth data={data} />;
          }
        },
      }),
    }),
    [growthHistoryData, growthIsLoading, growthIsFetching, growthIsError, growthError],
  );

  return growthHistoryConfig;
};

export default useGetGrowthHistoryConfig;
